"use client";

import { useStartrackFilterTracking } from "@/lib/startrack/hooks/useStartrackFilterTracking";
import useMapClickTracking from "@/lib/startrack/hooks/useMapClickTracking";
import { usePlatformDataFilterTracking } from "@/lib/PlatformData/hooks/usePlatformDataFilterTracking";
import { usePlatformDataSearchTracking } from "@/lib/PlatformData/hooks/usePlatformDataSearchTracking";
import React from "react";
import styled from "styled-components";
import media from "@/styles/mediaQueries";
import TrustedShops from "@/components/Stateful/TrustBadge/TrustBadge";
import { useBiffInitialization } from "@/lib/AdManagment/useBiffInitialization";
import { getIsLoggedIn } from "@/lib/Helpers/userHelpers";
import FilterBar from "@/components/Stateful/Filters/FilterBar/FilterBar";
import { hotelDetailSortByOptions } from "@/lib/StaticData/sortOptions";
import { WhitelabelNames } from "@/whitelabel/helpers/types";
import { channel } from "@/lib/Types/Channel";
import { AppConfig } from "@/lib/Types/AppConfig";

const Container = styled.main(({ theme }) => ({
  "#upper-section": {
    backgroundColor: theme.kallisto.color.background.static.backgroundPage,
    boxShadow: theme.kallisto.shadow.mediumDownShadow,
    zIndex: 1,
    position: "relative",
    "#upper-section-content": {
      display: "grid",
      gridTemplateAreas: `
        "breadcrumbs"
        "gallery"
        "title"
        "info"
        "priceCurve"
        "mealtypeTabs"
      `,
      maxWidth: "1260px",
      margin: "0 auto",
      padding: "24px 16px 0 16px",
      gap: "1rem",
      gridTemplateColumns: "1fr",

      ...media.screenMdMin({
        gridTemplateAreas: `
          "breadcrumbs"
          "title"
          "gallery"
          "info"
          "priceCurve"
          "mealtypeTabs"
        `,
      }),
    },
  },
  "#lower-section": {
    backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
    "#lower-section-content": {
      display: "grid",
      gridTemplateAreas: `
        "desktopFilters"
        "offers"
      `,
      maxWidth: "1260px",
      margin: "0 auto",
      padding: "24px 16px 24px 16px",
      gap: "1rem",
      gridTemplateColumns: "1fr",
    },
  },

  ".desktop-filters": {
    display: "none",
    ...media.screenMdMin({
      display: "block",
    }),
  },
}));

interface HotelLayoutClientProps {
  children: React.ReactNode;
  appConfig: AppConfig;
}

export default function HotelLayoutClient({
  children,
  appConfig,
}: HotelLayoutClientProps) {
  const { WlConfig, envConfig, viewMode } = appConfig;
  const { hasBannerGPT } = WlConfig;

  const isHP =
    WlConfig.name === WhitelabelNames.holidaypirates ||
    WlConfig.name === WhitelabelNames.holidaypiratesAustrian;

  usePlatformDataFilterTracking(appConfig);
  usePlatformDataSearchTracking(appConfig);
  useStartrackFilterTracking(appConfig);
  useMapClickTracking(appConfig);

  // Initialize BIFF globally for this layout
  useBiffInitialization({
    hasBannerGPT: !!hasBannerGPT,
    isLoggedIn: getIsLoggedIn(),
    isProduction: envConfig?.currentEnv === "production",
    envConfig,
  });

  const availableHotelDetailSortOptions =
    viewMode.channel === channel.HOTEL
      ? hotelDetailSortByOptions.filter(({ value }) => value !== "AIRPORT")
      : hotelDetailSortByOptions;

  return (
    <>
      <FilterBar
        key={`filter-bar-hotel-detail`}
        sortOptions={availableHotelDetailSortOptions}
        defaultSortOption={availableHotelDetailSortOptions[0]}
        hasFlexibleCancellation={WlConfig.hasFlexibleCancellation.hotelDetail}
        hasSpecialDealsFilter={true}
        hasMapButton={true}
        isHP={isHP}
        isHotelDetailPage={true}
        appConfig={appConfig}
        isHotelOnly={viewMode.channel === channel.HOTEL}
      />

      <Container>
        {children}
        {WlConfig.hasTrustedShops && <TrustedShops />}
      </Container>
    </>
  );
}
